<template>
  <div class="space-y-6">
    <ActiveCalendarBanner :calendar="activeCalendar" />

    <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div class="border-b border-gray-100 px-5 pt-4 pb-0">
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-base font-bold text-gray-900">Sessions &amp; {{ termLabel }}s</h2>
          <div class="flex items-center gap-2">
            <IconField>
              <InputIcon class="pi pi-search" />
              <InputText v-model="searchQuery" placeholder="Search..." size="small" class="w-40" />
            </IconField>
            <Button size="small" outlined severity="secondary"
              :icon="showCreateSession ? 'pi pi-minus' : 'pi pi-plus'"
              :label="showCreateSession ? 'Cancel' : 'New Session'" @click="showCreateSession = !showCreateSession"
              data-testid="btn-toggle-create-session" />
            <Button size="small" :icon="showCreateCalendar ? 'pi pi-minus' : 'pi pi-plus'"
              :label="showCreateCalendar ? 'Cancel' : `New ${termLabel === 'Semester' ? 'Semester' : 'Calendar'}`"
              @click="showCreateCalendar = !showCreateCalendar" data-testid="btn-toggle-create-calendar" />
          </div>
        </div>
        <div class="flex gap-1">
          <button v-for="tab in subTabs" :key="tab.key" @click="activeSubTab = tab.key" :class="[
            'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors',
            activeSubTab === tab.key
              ? 'bg-gray-50 text-primary border border-b-0 border-gray-200'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50',
          ]">
            {{ tab.label }}
            <span v-if="tab.count !== undefined" class="ml-1.5 text-xs px-1.5 py-0.5 rounded-full"
              :class="activeSubTab === tab.key ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-500'">
              {{ tab.count }}
            </span>
          </button>
        </div>
      </div>

      <CreateSessionForm :visible="showCreateSession" @created="onSessionCreated" />
      <CreateCalendarForm :visible="showCreateCalendar" @created="onCalendarCreated" />

      <!-- Content -->
      <div class="p-5">
        <template v-if="loadingSchool || sessionLoading">
          <div class="flex items-center justify-center py-12">
            <i class="pi pi-spin pi-spinner text-3xl text-primary"></i>
          </div>
        </template>
        <template v-else>
          <!-- Sessions Tab -->
          <div v-if="activeSubTab === 'sessions'">
            <div v-if="!sessions.length" class="text-center py-12 text-gray-400">
              <i class="pi pi-bookmark text-4xl mb-3 block"></i>
              <p class="text-sm">No sessions created yet</p>
              <Button label="Create your first session" size="small" text class="mt-2"
                @click="showCreateSession = true" />
            </div>
            <div v-else class="space-y-3">
              <div v-for="session in paginatedSessions" :key="session.id"
                class="group border border-gray-100 rounded-lg hover:border-gray-200 transition-colors">
                <div class="flex items-center justify-between p-4 cursor-pointer"
                  @click="toggleSessionExpand(session.id)">
                  <div class="flex items-center gap-3">
                    <div class="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                      <i class="pi pi-bookmark text-sm"></i>
                    </div>
                    <div>
                      <p class="text-sm font-semibold text-gray-900">{{ session.name }}</p>
                      <p class="text-xs text-gray-500">{{ formatDate(session.started_at) }} &mdash; {{
                        formatDate(session.ended_at) }}</p>
                    </div>
                  </div>
                  <div class="flex items-center gap-3">
                    <button
                      class="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md hover:bg-primary/10 text-primary"
                      title="Edit session" @click.stop="startEditSession(session)">
                      <i class="pi pi-pencil text-xs"></i>
                    </button>
                    <span class="text-xs text-gray-400">{{ session.calendars.length }} {{
                      session.calendars.length === 1 ? 'calendar' : 'calendars' }}</span>
                    <i class="pi pi-chevron-down text-xs text-gray-400 transition-transform"
                      :class="{ 'rotate-180': expandedSessions.has(session.id) }"></i>
                  </div>
                </div>

                <!-- Edit Session Inline -->
                <div v-if="editingSessionId === session.id" class="px-4 pb-4 border-t border-gray-100">
                  <form @submit.prevent="handleUpdateSession"
                    class="flex flex-col lg:flex-row items-end gap-3 pt-3 bg-primary/10 -mx-4 px-4 pb-3">
                    <div class="flex-1 w-full">
                      <label class="block text-xs font-medium text-gray-600 mb-1">Session Name</label>
                      <InputText v-model="editSessionForm.name" class="w-full" />
                    </div>
                    <div class="flex-1 w-full">
                      <label class="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                      <Calendar v-model="editSessionForm.started_at" dateFormat="yy-mm-dd" class="w-full" />
                    </div>
                    <div class="flex-1 w-full">
                      <label class="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                      <Calendar v-model="editSessionForm.ended_at" dateFormat="yy-mm-dd" class="w-full" />
                    </div>
                    <div class="flex gap-2">
                      <Button type="submit" icon="pi pi-check" size="small" :loading="isSavingSession"
                        severity="success" />
                      <Button type="button" icon="pi pi-times" size="small" severity="secondary" outlined
                        @click="editingSessionId = null" />
                    </div>
                  </form>
                </div>

                <!-- Expanded Calendars -->
                <div v-if="expandedSessions.has(session.id) && session.calendars.length" class="px-4 pb-4">
                  <div class="ml-4 pl-4 border-l-2 border-gray-100 space-y-2">
                    <div v-for="cal in session.calendars" :key="cal.id"
                      class="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                      <div class="flex items-center gap-3">
                        <div class="w-7 h-7 rounded-md flex items-center justify-center"
                          :class="cal.is_active ? 'bg-primary/10 text-primary' : 'bg-gray-200 text-gray-500'">
                          <i class="pi pi-calendar text-xs"></i>
                        </div>
                        <div>
                          <p class="text-sm font-medium text-gray-800">{{ cal.term_name }}</p>
                          <p class="text-xs text-gray-500">{{ cal.started_at }} &mdash; {{ cal.ended_at }}
                            <span v-if="cal.total_school_days" class="ml-2 text-gray-400">&bull; {{
                              cal.total_school_days }} days</span>
                          </p>
                        </div>
                      </div>
                      <div class="flex items-center gap-2">
                        <button
                          class="p-1.5 rounded-md hover:bg-primary/10 text-primary transition-colors"
                          title="Edit calendar" @click.stop="startEditCalendar(cal)">
                          <i class="pi pi-pencil text-xs"></i>
                        </button>
                        <span v-if="cal.is_active"
                          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          <span class="w-1.5 h-1.5 rounded-full bg-primary"></span> Active
                        </span>
                        <Button v-else label="Set Active" size="small" text severity="secondary"
                          :loading="markingActive === cal.id"
                          @click.stop="handleSetActive(cal.id, cal.is_active)"
                          data-testid="btn-settings-calendar-set-active" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Session Pagination -->
              <div v-if="totalSessionPages > 1" class="flex items-center justify-between pt-4 border-t border-gray-100">
                <p class="text-xs text-gray-500">
                  Showing {{ (sessionPage - 1) * PAGE_SIZE + 1 }}&ndash;{{ Math.min(sessionPage * PAGE_SIZE,
                  filteredSessions.length) }}
                  of {{ filteredSessions.length }} sessions
                </p>
                <div class="flex gap-1">
                  <Button icon="pi pi-chevron-left" size="small" text severity="secondary"
                    :disabled="sessionPage <= 1" @click="sessionPage--" />
                  <Button v-for="p in totalSessionPages" :key="p" :label="String(p)" size="small"
                    :text="p !== sessionPage" :severity="p === sessionPage ? undefined : 'secondary'"
                    @click="sessionPage = p" />
                  <Button icon="pi pi-chevron-right" size="small" text severity="secondary"
                    :disabled="sessionPage >= totalSessionPages" @click="sessionPage++" />
                </div>
              </div>
            </div>
          </div>

          <!-- Calendars Tab -->
          <div v-if="activeSubTab === 'calendars'">
            <div v-if="!calendarTableData.length" class="text-center py-12 text-gray-400">
              <i class="pi pi-calendar text-4xl mb-3 block"></i>
              <p class="text-sm">No calendars created yet</p>
              <Button label="Create your first calendar" size="small" text class="mt-2"
                @click="showCreateCalendar = true" />
            </div>
            <div v-else class="overflow-x-auto">
              <table class="w-full text-sm">
                <thead>
                  <tr class="border-b border-gray-100">
                    <th class="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Session</th>
                    <th class="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">{{ termLabel }}</th>
                    <th class="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Start</th>
                    <th class="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">End</th>
                    <th class="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Days</th>
                    <th class="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                    <th class="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="cal in paginatedCalendars" :key="cal.id"
                    class="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td class="py-3 px-4 font-medium text-gray-900">{{ cal.session_name }}</td>
                    <td class="py-3 px-4 text-gray-700">{{ cal.term_name }}</td>
                    <td class="py-3 px-4 text-gray-600">{{ cal.started_at }}</td>
                    <td class="py-3 px-4 text-gray-600">{{ cal.ended_at }}</td>
                    <td class="py-3 px-4 text-center text-gray-600">{{ cal.total_school_days ?? '—' }}</td>
                    <td class="py-3 px-4 text-right">
                      <span v-if="cal.is_active"
                        class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        <span class="w-1.5 h-1.5 rounded-full bg-primary"></span> Active
                      </span>
                      <Button v-else label="Set Active" size="small" text severity="secondary"
                        :loading="markingActive === cal.id" @click="handleSetActive(cal.id, cal.is_active)"
                        data-testid="btn-settings-calendar-set-active" />
                    </td>
                    <td class="py-3 px-4 text-right">
                      <button class="p-1.5 rounded-md hover:bg-primary/10 text-primary transition-colors"
                        title="Edit calendar" @click="startEditCalendar(cal)">
                        <i class="pi pi-pencil text-xs"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Calendar Pagination -->
              <div v-if="totalCalendarPages > 1"
                class="flex items-center justify-between pt-4 mt-2 border-t border-gray-100">
                <p class="text-xs text-gray-500">
                  Showing {{ (calendarPage - 1) * PAGE_SIZE + 1 }}&ndash;{{ Math.min(calendarPage * PAGE_SIZE,
                  filteredCalendars.length) }}
                  of {{ filteredCalendars.length }} calendars
                </p>
                <div class="flex gap-1">
                  <Button icon="pi pi-chevron-left" size="small" text severity="secondary"
                    :disabled="calendarPage <= 1" @click="calendarPage--" />
                  <Button v-for="p in totalCalendarPages" :key="p" :label="String(p)" size="small"
                    :text="p !== calendarPage" :severity="p === calendarPage ? undefined : 'secondary'"
                    @click="calendarPage = p" />
                  <Button icon="pi pi-chevron-right" size="small" text severity="secondary"
                    :disabled="calendarPage >= totalCalendarPages" @click="calendarPage++" />
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <EditCalendarDialog v-model:visible="showEditCalendarDialog" :calendar="editingCalendar" @updated="onCalendarUpdated" />
  </div>
</template>

<script setup lang="ts">
import { useSessions } from '@/composables/queries/useSessions';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { useSchoolMutations } from '@/composables/mutations/school/useSchoolMutations';
import type { AcademicCalendar as AcademicCalendarType, Session } from '@/types/composition-functions';
import ActiveCalendarBanner from './ActiveCalendarBanner.vue';
import CreateSessionForm from './CreateSessionForm.vue';
import CreateCalendarForm from './CreateCalendarForm.vue';
import EditCalendarDialog from './EditCalendarDialog.vue';

interface CalendarTableRow {
  id: string;
  session_name: string;
  term_name: string;
  started_at: string;
  ended_at: string;
  total_school_days?: number | null;
  is_active: boolean;
  next_calender_starts_at: string | null;
}

const PAGE_SIZE = 5;

const { setActiveAcademicCalendar, updateSession } = useSchoolMutations();
const { schoolSessions } = useSessions({ getSchoolSessions: { autoFetch: true } });
const { sessions, loading: sessionLoading, refetch: refetchSessions } = schoolSessions;
const { academicCalendars, loadingSchool, refetchSchool } = useSchoolQuery();
const { isTertiary } = useSchoolFeatures();

const termLabel = computed(() => isTertiary.value ? 'Semester' : 'Term');

// --- Search ---
const searchQuery = ref('');
watch(searchQuery, () => { sessionPage.value = 1; calendarPage.value = 1; });

// --- Sub-tab state ---
const activeSubTab = ref<'sessions' | 'calendars'>('sessions');
const subTabs = computed(() => [
  { key: 'sessions' as const, label: 'Sessions', count: filteredSessions.value.length },
  { key: 'calendars' as const, label: termLabel.value + 's', count: filteredCalendars.value.length },
]);

// --- Session expand state ---
const expandedSessions = ref(new Set<string>());
function toggleSessionExpand(id: string) {
  if (expandedSessions.value.has(id)) {
    expandedSessions.value.delete(id);
  } else {
    expandedSessions.value.add(id);
  }
}

// --- Toggles ---
const showCreateSession = ref(false);
const showCreateCalendar = ref(false);

// --- Pagination ---
const sessionPage = ref(1);
const calendarPage = ref(1);

const totalSessionPages = computed(() => Math.ceil(filteredSessions.value.length / PAGE_SIZE));
const totalCalendarPages = computed(() => Math.ceil(filteredCalendars.value.length / PAGE_SIZE));

const paginatedSessions = computed(() => {
  const start = (sessionPage.value - 1) * PAGE_SIZE;
  return filteredSessions.value.slice(start, start + PAGE_SIZE);
});

const paginatedCalendars = computed(() => {
  const start = (calendarPage.value - 1) * PAGE_SIZE;
  return filteredCalendars.value.slice(start, start + PAGE_SIZE);
});

// --- Edit Session Inline ---
const editingSessionId = ref<string | null>(null);
const isSavingSession = ref(false);
const editSessionForm = ref({
  name: '',
  started_at: undefined as Date | undefined,
  ended_at: undefined as Date | undefined,
});

function startEditSession(session: { id: string; name?: string; started_at?: string; ended_at?: string }) {
  editingSessionId.value = session.id;
  editSessionForm.value = {
    name: session.name || '',
    started_at: session.started_at ? new Date(session.started_at) : undefined,
    ended_at: session.ended_at ? new Date(session.ended_at) : undefined,
  };
}

async function handleUpdateSession() {
  if (!editingSessionId.value || !editSessionForm.value.name) return;
  isSavingSession.value = true;
  try {
    await updateSession({
      id: editingSessionId.value,
      name: editSessionForm.value.name,
      started_at: editSessionForm.value.started_at ? new Date(editSessionForm.value.started_at).toISOString() : null,
      ended_at: editSessionForm.value.ended_at ? new Date(editSessionForm.value.ended_at).toISOString() : null,
    });
    editingSessionId.value = null;
    await refetchSessions();
  } catch {
    // toast handled in composable
  } finally {
    isSavingSession.value = false;
  }
}

// --- Set Active ---
const markingActive = ref<string | null>(null);
async function handleSetActive(id: string, currentlyActive: boolean) {
  if (currentlyActive) return;
  markingActive.value = id;
  try {
    await setActiveAcademicCalendar(id);
    await refetchSchool();
  } catch {
    // toast handled in composable
  } finally {
    markingActive.value = null;
  }
}

// --- Edit Calendar ---
const showEditCalendarDialog = ref(false);
const editingCalendar = ref<CalendarTableRow | null>(null);

function startEditCalendar(cal: CalendarTableRow) {
  editingCalendar.value = cal;
  showEditCalendarDialog.value = true;
}

// --- Widget event handlers ---
async function onSessionCreated() {
  await refetchSessions();
  showCreateSession.value = false;
}

async function onCalendarCreated() {
  await refetchSchool();
  showCreateCalendar.value = false;
}

async function onCalendarUpdated() {
  await refetchSchool();
}

// --- Computed Data ---
const formatDate = (dt: string) => {
  if (!dt) return '—';
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const calendarTableData = computed(() => {
  const records = academicCalendars.value || [];
  return records.map((cal: AcademicCalendarType) => ({
    id: cal.id,
    session_name: cal.session?.name || '',
    term_name: cal.termStructure?.name || '',
    started_at: formatDate(cal.started_at),
    ended_at: formatDate(cal.ended_at),
    total_school_days: cal.total_school_days,
    is_active: cal.is_active,
    next_calender_starts_at: cal.next_calender_starts_at ? formatDate(cal.next_calender_starts_at) : null,
  })).sort((a: CalendarTableRow, b: CalendarTableRow) => {
    if (!a.started_at) return 1;
    if (!b.started_at) return -1;
    return new Date(b.started_at).getTime() - new Date(a.started_at).getTime();
  });
});

const activeCalendar = computed(() => calendarTableData.value.find((c: CalendarTableRow) => c.is_active));

const sessionsWithCalendars = computed(() => {
  const cals = calendarTableData.value;
  return sessions.value.map((s: Session) => ({
    ...s,
    calendars: cals.filter((c: CalendarTableRow) => c.session_name === s.name),
  }));
});

const filteredSessions = computed(() => {
  const q = searchQuery.value.toLowerCase().trim();
  if (!q) return sessionsWithCalendars.value;
  return sessionsWithCalendars.value.filter(s =>
    s.name?.toLowerCase().includes(q)
    || s.calendars.some((c: CalendarTableRow) => c.term_name.toLowerCase().includes(q)),
  );
});

const filteredCalendars = computed(() => {
  const q = searchQuery.value.toLowerCase().trim();
  if (!q) return calendarTableData.value;
  return calendarTableData.value.filter((c: CalendarTableRow) =>
    c.session_name.toLowerCase().includes(q)
    || c.term_name.toLowerCase().includes(q),
  );
});

watch(sessionsWithCalendars, (newVal) => {
  for (const s of newVal) {
    if (s.calendars.some((c: CalendarTableRow) => c.is_active)) {
      expandedSessions.value.add(s.id);
    }
  }
}, { immediate: true });
</script>
